// src/Routes/Pricing/PricingPage.tsx
import React, { useContext, useEffect } from "react";
import { FaCheckCircle, FaArrowRight } from "react-icons/fa";
import { HashLoader } from "react-spinners";
import SidebarLayout from "../../Components/Layout/SidebarLayout";
import config from "../../config";
import { useAuthenticatedFetch } from "../../Hooks/useFetch";
import { GoogleAnalyticsContext } from "../../Hooks/GoogleAnalyticsContext";
import { YippityUserContext } from "../../Hooks/YippityUserContext";

interface PricingTierProps {
  name: string;
  price: string;
  features: string[];
  isPopular?: boolean;
  isCurrent?: boolean;
  onSelectPlan?: () => void;
  isLoading?: boolean;
}

const PricingTier: React.FC<PricingTierProps> = ({
  name,
  price,
  features,
  isPopular = false,
  isCurrent = false,
  onSelectPlan = () => {},
  isLoading = false,
}) => {
  return (
    <div
      className={`
      bg-white rounded-lg shadow-sm overflow-hidden transition-all hover:shadow
      ${isPopular ? "border-2 border-yellow-400" : "border border-gray-100"}
    `}
    >
      {isPopular && (
        <div className="bg-yellow-400 text-gray-800 text-xs font-bold py-1 text-center">
          RECOMMENDED
        </div>
      )}

      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-800">{name}</h3>

        <div className="mt-4 flex items-baseline">
          <span className="text-3xl font-extrabold text-gray-900">
            ${price}
          </span>
          <span className="ml-1 text-gray-500">/month</span>
        </div>

        <div className="mt-6 space-y-3">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start">
              <FaCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="ml-3 text-sm text-gray-700">{feature}</p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <button
            onClick={onSelectPlan}
            disabled={isCurrent || isLoading}
            className={`
              w-full py-3 px-4 rounded-md font-medium focus:outline-none
              ${
                isCurrent
                  ? "bg-gray-100 text-gray-800 cursor-default"
                  : "bg-yellow-400 text-gray-800 hover:bg-yellow-500"
              }
              ${isLoading ? "opacity-75 cursor-wait" : ""}
            `}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <HashLoader size={20} color="#4B5563" />
                <span className="ml-2">Processing...</span>
              </div>
            ) : isCurrent ? (
              "Current Plan"
            ) : (
              <div className="flex items-center justify-center">
                <span>Select Plan</span>
                <FaArrowRight className="ml-2" />
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const PricingPage: React.FC = () => {
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const { callAPI, res, isLoading } = useAuthenticatedFetch({
    url: config.API_URI + `/create-checkout-session`,
    method: "POST",
  });
  const { user, isSubscribed } = useContext(YippityUserContext);
  const [checkoutUrl, setCheckoutUrl] = React.useState<string | null>(null);

  // Pre-fetch checkout URL on component mount
  useEffect(() => {
    if (!isSubscribed) {
      callAPI(
        JSON.stringify({
          priceId: config.PRICE_ID,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (res.statusCode === 200) {
      setCheckoutUrl(res.data.url);
    }
  }, [res]);

  const handleSubscribe = () => {
    logEvent("Interaction", "Subscribe");
    if (checkoutUrl) {
      window.open(checkoutUrl, "_self");
    }
  };

  return (
    <SidebarLayout>
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-800">Pricing</h1>
          <p className="mt-2 text-gray-600">
            Unlock unlimited access to our complete suite of AI study tools
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <PricingTier
            name="Free"
            price="0"
            features={[
              "1 AI generation per month",
              "Basic question types",
              "Save and share quizzes",
              "Limited tool access",
            ]}
            isCurrent={!isSubscribed}
          />

          <PricingTier
            name="Premium"
            price="7.99"
            features={[
              "Unlimited generations",
              "All AI tools included",
              "Priority access to new features",
              "Enhanced AI accuracy",
              "Early adopter price lock",
            ]}
            isPopular={true}
            isCurrent={isSubscribed}
            onSelectPlan={handleSubscribe}
            isLoading={isLoading}
          />
        </div>

        <div className="mt-12 bg-yellow-50 rounded-lg p-6 border border-yellow-100">
          <h3 className="text-lg font-medium text-yellow-800">
            ✨ Early Access Pricing
          </h3>
          <p className="mt-2 text-yellow-700">
            Subscribe now to lock in our current pricing forever, even as we add
            more AI tools and features.
          </p>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default PricingPage;
